import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { IoNotifications, IoClose, IoChatbubble, IoHeart, IoPersonAdd, IoBook, IoGift, IoMegaphone } from 'react-icons/io5';
import { playNotificationSound } from '../utils/sounds';
import './Notifications.css';

const NOTIF_CONFIG = {
  comment: { icon: IoChatbubble, color: '#c8956c', getText: (n) => <><strong>{n.fromName}</strong> a commenté <strong>{n.bookTitle}</strong></> },
  like: { icon: IoHeart, color: '#c27a7a', getText: (n) => <><strong>{n.fromName}</strong> a aimé <strong>{n.bookTitle}</strong></> },
  follow: { icon: IoPersonAdd, color: '#7a9e7e', getText: (n) => <><strong>{n.fromName}</strong> s'est abonné à ton profil</> },
  new_book: { icon: IoBook, color: '#8b6f8e', getText: (n) => <><strong>{n.fromName}</strong> a publié un nouveau livre : <strong>{n.bookTitle}</strong></> },
  plumes: { icon: IoGift, color: '#f59e0b', getText: (n) => <><strong>{n.fromName}</strong> t'a envoyé des plumes 🪶</> },
  mention: { icon: IoChatbubble, color: '#7a9ab5', getText: (n) => <><strong>{n.fromName}</strong> t'a mentionné</> },
  info: { icon: IoMegaphone, color: '#888', getText: (n) => <>{n.message || 'Nouvelle notification'}</> },
};

export default function Notifications() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [prevUnread, setPrevUnread] = useState(0);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.id),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const newUnread = data.filter(n => !n.read).length;

      if (newUnread > prevUnread && prevUnread >= 0 && data.length > 0) {
        playNotificationSound();
      }
      setPrevUnread(newUnread);
      setNotifications(data);
    });

    return () => unsubscribe();
  }, [user]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = async (notifId) => {
    await updateDoc(doc(db, 'notifications', notifId), { read: true });
  };

  const markAllAsRead = async () => {
    const batch = writeBatch(db);
    notifications.filter(n => !n.read).forEach(n => {
      batch.update(doc(db, 'notifications', n.id), { read: true });
    });
    await batch.commit();
  };

  const handleClick = (notif) => {
    markAsRead(notif.id);
    setIsOpen(false);

    switch (notif.type) {
      case 'comment':
      case 'like':
      case 'new_book':
        if (notif.bookId) navigate('/book/' + notif.bookId);
        break;
      case 'follow':
        if (notif.fromId) navigate('/author/' + notif.fromId);
        break;
      case 'plumes':
        navigate('/profile');
        break;
      default:
        break;
    }
  };

  const timeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return "à l'instant";
    if (seconds < 3600) return Math.floor(seconds / 60) + ' min';
    if (seconds < 86400) return Math.floor(seconds / 3600) + ' h';
    return Math.floor(seconds / 86400) + ' j';
  };

  return (
    <div className="notif-container">
      <motion.button
        className="notif-bell"
        onClick={() => setIsOpen(!isOpen)}
        whileTap={{ scale: 0.9 }}
      >
        <IoNotifications />
        {unreadCount > 0 && (
          <motion.span
            className="notif-badge"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500 }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.span>
        )}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="notif-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              className="notif-panel"
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <div className="notif-header">
                <h3>Notifications</h3>
                <div className="notif-header-actions">
                  {unreadCount > 0 && (
                    <button className="notif-mark-all" onClick={markAllAsRead}>
                      Tout marquer lu
                    </button>
                  )}
                  <button className="notif-close" onClick={() => setIsOpen(false)}>
                    <IoClose />
                  </button>
                </div>
              </div>

              <div className="notif-list">
                {notifications.length > 0 ? (
                  notifications.slice(0, 30).map(notif => {
                    const config = NOTIF_CONFIG[notif.type] || NOTIF_CONFIG.info;
                    const Icon = config.icon;
                    return (
                      <motion.div
                        key={notif.id}
                        className={'notif-item ' + (!notif.read ? 'notif-item-unread' : '')}
                        onClick={() => handleClick(notif)}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="notif-icon-wrapper" style={{ background: config.color + '18', color: config.color }}>
                          <Icon />
                        </div>
                        <div className="notif-content">
                          <p className="notif-text">{config.getText(notif)}</p>
                          <span className="notif-time">{timeAgo(notif.createdAt)}</span>
                        </div>
                        {!notif.read && <div className="notif-dot" />}
                      </motion.div>
                    );
                  })
                ) : (
                  <div className="notif-empty">
                    <span>🔔</span>
                    <p>Aucune notification</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}